/** @format */
import isPropValid from '@emotion/is-prop-valid';
import React, { FunctionComponent } from 'react';
import styled from 'styled-components';

import { styles } from './dot.styles';

export enum DotSize {
    SMALL = 'small',
    MEDIUM = 'medium',
}

export enum DotColor {
    STANDARD = 'standard',
    INVERTED = 'inverted',
}

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface DotProps {
    /**
     * Whether the dot is selected
     * @default false
     */
    selected?: boolean;

    /**
     * Whether the dot is disabled
     *
     * @default false
     */
    disabled?: boolean;

    /**
     * The size of the dot, one of:
     * 1. SMALL
     * 2. MEDIUM
     *
     * @default MEDIUM
     */
    size?: DotSize;

    /**
     * The colour of the dot, one of:
     * 1. STANDARD
     * 2. INVERTED
     *
     * @default STANDARD
     */
    dotColor?: DotColor;

    /**
     * An optional tabindex parmeter for the html element
     */
    tabIndex?: number;

    /**
     * The label for screen readers
     */
    'aria-label'?: string;

    /**
     * Selection ID for test purposes.
     */
    'data-testid'?: string;

    /**
     * Custom className
     */
    className?: string;

    /**
     * Mouse click event handler.
     *
     * @param event {MouseEvent<HTMLElement>} The mouse event
     */
    onClick?: (event: React.MouseEvent<HTMLElement>) => void;

    /**
     * Element blur event handler.
     *
     * @param event {FocusEvent<HTMLElement>} The blur event
     */
    onBlur?: (event: React.FocusEvent<HTMLElement>) => void;

    /**
     * Element focus event handler.
     *
     * @param event {FocusEvent<HTMLElement>} The focus event
     */
    onFocus?: (event: React.FocusEvent<HTMLElement>) => void;
}

const StyledDot = styled('button').withConfig({
    shouldForwardProp: (prop) => isPropValid(prop),
})`
    ${styles}
`;

export const Dot: FunctionComponent<DotProps> = ({
    size = DotSize.MEDIUM,
    dotColor = DotColor.STANDARD,
    selected = false,
    ...otherProps
}: DotProps) => {
    return (
        <StyledDot
            size={size}
            dotColor={dotColor}
            aria-selected={selected}
            {...otherProps}
        />
    );
};
